import React from "react";
import { useI18n } from "../../lib/i18n.jsx";
import { track } from "../../lib/analytics.js";

/**
 * Tres rutas de comprador.
 * Agricultor, cooperativa y distribuidor compran por razones distintas.
 * Cada perfil entra por su propio mensaje y su propio CTA.
 */
export default function Perfiles({ onRequestQuote, onOpenDistribuye, onScrollProducts }) {
  const { t } = useI18n();

  const perfiles = [
    {
      key: "agricultor",
      icon: "fa-tractor",
      action: () => {
        track("perfil_cta", { perfil: "agricultor" });
        onScrollProducts?.();
      },
    },
    {
      key: "cooperativa",
      icon: "fa-warehouse",
      action: () => {
        track("perfil_cta", { perfil: "cooperativa" });
        onRequestQuote?.();
      },
    },
    {
      key: "distribuidor",
      icon: "fa-handshake",
      action: () => {
        track("perfil_cta", { perfil: "distribuidor" });
        onOpenDistribuye?.();
      },
    },
  ];

  return (
    <section
      id="perfiles"
      className="relative py-24 lg:py-32 overflow-hidden bg-[#133322]"
      data-testid="perfiles-section"
    >
      {/* Fondo: naranjal al atardecer */}
      <video
        src="/video/citricos.mp4"
        poster="/video/citricos-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-[#133322]/75 via-[#133322]/25 to-[#133322]/60"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="eu-data-label mb-4 !text-[#9ead9b]">
            <i className="fa-solid fa-route mr-2"></i>
            {t.perfiles.eyebrow}
          </div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#f7f7f4] leading-tight tracking-tight">
            {t.perfiles.title}
          </h2>
          <p className="mt-4 text-[#9ead9b] text-base lg:text-lg">
            {t.perfiles.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {perfiles.map((p, idx) => {
            const data = t.perfiles.items[p.key];
            return (
              <div
                key={p.key}
                className="bg-[#f7f7f4] rounded-2xl p-8 flex flex-col group shadow-[0_20px_50px_rgba(0,0,0,0.25)] hover:-translate-y-1 transition-transform"
                data-testid={`perfil-${p.key}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="h-11 w-11 rounded-full bg-[#b45f3e]/10 flex items-center justify-center">
                    <i className={`fa-solid ${p.icon} text-[#b45f3e]`}></i>
                  </span>
                  <span className="font-mono-eu text-[0.65rem] tracking-[0.2em] text-[#9ead9b]">
                    0{idx + 1}
                  </span>
                </div>

                <h3 className="font-display text-2xl text-[#133322] leading-snug">
                  {data.title}
                </h3>
                <p className="mt-3 text-[#5c6b61] text-[0.95rem] leading-relaxed">
                  {data.desc}
                </p>

                <ul className="mt-6 space-y-2.5 flex-grow">
                  {data.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#5c6b61]">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#9ead9b] flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={p.action}
                  className="mt-8 self-start font-mono-eu text-[0.7rem] tracking-[0.15em] uppercase text-[#133322] border-b border-[#b45f3e] pb-1 hover:text-[#b45f3e] transition-colors"
                  data-testid={`perfil-cta-${p.key}`}
                >
                  {data.cta} <i className="fa-solid fa-arrow-right ml-1 text-[0.65rem]"></i>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
